import type { ItemResponse } from '@finn-no/mobility-item-insertion-shared';
import { generateTitle } from '@finn-no/mobility-item-insertion-shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { getHuckConfig } from '../utils/getHuckConfig.js';

type UpdateRequest = { data: ItemResponse['data']; commit?: boolean };

const convertEmptyStringsToUndefined = <T extends Record<string, any>>(obj: T): T => {
  const result: Record<string, any> = {};

  for (const key of Object.keys(obj)) {
    if (obj[key] === '') {
      result[key] = undefined;
    } else if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
      result[key] = convertEmptyStringsToUndefined(obj[key]);
    } else {
      result[key] = obj[key];
    }
  }

  return result as T;
};

const clearStreetAddressIfPrivateUser = (
  data: ItemResponse['data'],
  orgId: string | undefined,
): ItemResponse['data'] => {
  if (!orgId) {
    return {
      ...data,
      address: {
        ...data.address,
        street: undefined,
      },
    };
  }
  return data;
};

export const useItemUpdateMutation = (initialETag: string) => {
  const queryClient = useQueryClient();
  const etag = useRef(initialETag);
  const committed = useRef(false);

  const config = getHuckConfig();

  const ensureTitle = (data: ItemResponse['data']) => {
    return data.title ?? generateTitle(config.brand, config.model, config.adInputLookup, data) ?? '';
  };

  const sanitizeData = (data: ItemResponse['data']) => {
    const withoutEmptyStrings = convertEmptyStringsToUndefined(data);
    const withTitle = {
      ...withoutEmptyStrings,
      title: ensureTitle(withoutEmptyStrings),
    };
    return clearStreetAddressIfPrivateUser(withTitle, config.orgId);
  };

  const updateItemMutation = useMutation({
    mutationFn: async (nextUpdate: UpdateRequest) => {
      if (!nextUpdate.commit && committed.current) {
        // Do not allow saving after committing
        console.error('Attempt to save after committing');
        return;
      }

      const sanitizedData = sanitizeData(nextUpdate.data);
      const response = await fetch(
        `${config.root}/api/items/${config.itemId}${config.orgId ? `?orgId=${config.orgId}` : ''}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'If-Match': etag.current,
            'x-csrf-token': config.csrfToken,
          },
          body: JSON.stringify({ ...nextUpdate, data: sanitizedData }),
        },
      );

      if (!response.ok && response.status !== 409) {
        throw new Error('Failed to update item', { cause: response.status });
      }

      const data = (await response.json()) as ItemResponse;
      return { ...data, etag: response.headers.get('etag') };
    },
    mutationKey: ['itemUpdate', config.itemId],
    scope: { id: `item:${config.itemId}` }, // Ensures that all updates are processed in series
    retry: (_count, err: unknown) => {
      const cause = (err as { cause?: unknown })?.cause;
      const code = typeof cause === 'number' ? cause : undefined;
      const is4xx = code != null && code >= 400 && code < 500;
      return !is4xx;
    },
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 30000), // Retry with exponential backoff to max of every 30 seconds
    onSuccess: async (data, updateRequest) => {
      if (data?.etag) {
        etag.current = data.etag;
      }

      if (updateRequest.commit && (!data?.violations || data.violations.length === 0)) {
        committed.current = true;
      }
    },
  });

  const updateItem = async ({ data, commit }: UpdateRequest) => {
    // This ensure that any non-commit will throw away other pending non-commits, as the latest will always contain all changes
    if (!commit) {
      const cache = queryClient.getMutationCache();
      for (const mutation of cache.getAll()) {
        const isNonCommit = (mutation.state.variables as UpdateRequest | undefined)?.commit === false;
        const isSameScope = mutation.options.scope?.id === `item:${config.itemId}`;
        const isSameKey =
          Array.isArray(mutation.options.mutationKey) && mutation.options.mutationKey[0] === 'itemUpdate';
        const isPending = mutation.state.status === 'pending' && mutation.state.isPaused === true;
        if (isSameKey && isSameScope && isPending && isNonCommit) {
          cache.remove(mutation);
        }
      }
    }
    return updateItemMutation.mutateAsync({ data, commit });
  };

  return updateItem;
};
